import { useMemo, useState } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, GripVertical, Plus, Trash2 } from '@/components/ui/icons';
import type { Status, StatusType } from '@shared/types';
import { PALETTE, STATUS_GROUPS } from '@shared/constants';
import { newId } from '@shared/ids';
import { useDb, type Tables } from '@/lib/store';
import { setStatuses } from '@/lib/actions';
import { Modal } from '@/components/ui/Modal';
import { Popover } from '@/components/ui/Popover';
import { StatusIcon } from '@/components/fields/Status';

type Target = { type: 'space' | 'folder' | 'list'; id: string };

function inherited(db: Tables, target: Target): { statuses: Status[]; from: string } {
  if (target.type === 'list') {
    const l = db.lists[target.id];
    const f = l?.folderId ? db.folders[l.folderId] : null;
    if (f?.statuses?.length) return { statuses: f.statuses, from: f.name };
    return { statuses: db.spaces[l?.spaceId]?.statuses ?? [], from: db.spaces[l?.spaceId]?.name ?? 'Space' };
  }
  const f = db.folders[target.id];
  return { statuses: db.spaces[f?.spaceId]?.statuses ?? [], from: db.spaces[f?.spaceId]?.name ?? 'Space' };
}

function Row({ status, onChange, onDelete }: { status: Status; onChange: (s: Status) => void; onDelete: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: status.id });
  return (
    <div ref={setNodeRef} className="se-row" style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}>
      <span className="se-grip" {...attributes} {...listeners}>
        <GripVertical size={14} />
      </span>
      <Popover
        content={(close) => (
          <div className="menu" style={{ padding: 10 }}>
            <div className="swatches">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  className="swatch"
                  style={{ background: c }}
                  onClick={() => {
                    onChange({ ...status, color: c });
                    close();
                  }}
                >
                  {c === status.color && <Check size={12} />}
                </button>
              ))}
            </div>
          </div>
        )}
      >
        <button className="se-color">
          <StatusIcon type={status.type} color={status.color} size={16} />
        </button>
      </Popover>
      <input className="se-name" value={status.name} onChange={(e) => onChange({ ...status, name: e.target.value })} />
      <button className="icon-btn sm" onClick={onDelete} title="Delete status">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export function StatusEditorModal({ target, onClose }: { target: Target; onClose: () => void }) {
  const db = useDb((d) => d);
  const own = target.type === 'space' ? db.spaces[target.id]?.statuses : target.type === 'folder' ? db.folders[target.id]?.statuses : db.lists[target.id]?.statuses;
  const parent = target.type === 'space' ? null : inherited(db, target);
  const [useCustom, setUseCustom] = useState(target.type === 'space' || !!own?.length);
  const [draft, setDraft] = useState<Status[]>(() => (own?.length ? own : (parent?.statuses ?? [])).map((s) => ({ ...s })));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const groups = useMemo(() => STATUS_GROUPS.map((g) => ({ ...g, items: draft.filter((s) => g.types.includes(s.type)).sort((a, b) => a.orderIndex - b.orderIndex) })), [draft]);
  const name =
    target.type === 'space' ? db.spaces[target.id]?.name : target.type === 'folder' ? db.folders[target.id]?.name : db.lists[target.id]?.name;

  const reindex = (list: Status[]) => {
    // keep global order: group order, then position within group
    const out: Status[] = [];
    let i = 0;
    for (const g of STATUS_GROUPS) for (const s of list.filter((x) => g.types.includes(x.type))) out.push({ ...s, orderIndex: i++ });
    return out;
  };

  const onDragEnd = (groupTypes: StatusType[]) => (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const items = draft.filter((s) => groupTypes.includes(s.type)).sort((a, b) => a.orderIndex - b.orderIndex);
    const from = items.findIndex((s) => s.id === e.active.id);
    const to = items.findIndex((s) => s.id === e.over!.id);
    const moved = arrayMove(items, from, to);
    const rest = draft.filter((s) => !groupTypes.includes(s.type));
    setDraft(reindex([...rest, ...moved.map((s, i) => ({ ...s, orderIndex: i }))].sort((a, b) => a.orderIndex - b.orderIndex)));
  };

  const add = (type: StatusType) => {
    const s: Status = { id: newId(8), name: type === 'custom' ? 'new status' : type === 'closed' ? 'closed' : 'new status', color: PALETTE[(draft.length * 3) % PALETTE.length], type, orderIndex: 999 };
    setDraft(reindex([...draft, s].sort((a, b) => a.orderIndex - b.orderIndex)));
  };

  const save = () => {
    if (target.type !== 'space' && !useCustom) setStatuses(target, null);
    else {
      const cleaned = reindex(draft.filter((s) => s.name.trim()).map((s) => ({ ...s, name: s.name.trim() })));
      if (!cleaned.some((s) => s.type === 'closed')) cleaned.push({ id: newId(8), name: 'complete', color: '#008844', type: 'closed', orderIndex: cleaned.length });
      setStatuses(target, cleaned);
    }
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${name} statuses`}
      width={560}
      footer={
        <>
          <button className="btn outline" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Apply changes
          </button>
        </>
      }
    >
      {target.type !== 'space' && parent && (
        <div className="se-mode">
          <button className={`se-mode-opt ${!useCustom ? 'on' : ''}`} onClick={() => setUseCustom(false)}>
            Inherit from <strong>{parent.from}</strong>
          </button>
          <button
            className={`se-mode-opt ${useCustom ? 'on' : ''}`}
            onClick={() => {
              if (!useCustom && !own?.length) setDraft(parent.statuses.map((s) => ({ ...s, id: newId(8) })));
              setUseCustom(true);
            }}
          >
            Use custom statuses
          </button>
        </div>
      )}
      <div style={{ opacity: useCustom ? 1 : 0.5, pointerEvents: useCustom ? 'auto' : 'none' }}>
        {groups.map((g) => (
          <div key={g.key} className="se-group">
            <div className="se-group-head">
              <span>{g.label}</span>
              <button className="icon-btn xs" onClick={() => add(g.types[g.types.length - 1] === 'unstarted' ? 'unstarted' : g.types[0])} title="Add status">
                <Plus size={14} />
              </button>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd(g.types)}>
              <SortableContext items={g.items.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                {g.items.map((s) => (
                  <Row
                    key={s.id}
                    status={s}
                    onChange={(n) => setDraft(draft.map((x) => (x.id === n.id ? n : x)))}
                    onDelete={() => setDraft(draft.filter((x) => x.id !== s.id))}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {g.items.length === 0 && (
              <button className="se-add" onClick={() => add(g.types[0])}>
                <Plus size={14} /> Add status
              </button>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
